import { DEFAULT_TRACK_ID, getCustomTrackLayout, getTrackLength, worldPosition } from "./track";
import type { HazardState, KartState, RaceState, RaceVariantState } from "./types";

const HAZARD_LAYOUT = [
  [0.335, -4.8], [0.335, 4.8],
  [0.465, 0],
  [0.705, -4.5], [0.705, 4.5],
  [0.89, 0],
] as const;

const HAZARD_LANES: Record<RaceVariantState["hazardLayout"], readonly number[]> = {
  paired: [-4.8, 4.8, 0, -4.5, 4.5, 0],
  switchback: [0, 4.8, -4.8, 0, -4.5, 4.5],
  slalom: [-4.8, 0, 4.8, 4.5, 0, 4.5],
};

export function createHazards(trackId = DEFAULT_TRACK_ID, variant?: Pick<RaceVariantState, "index" | "hazardLayout">): HazardState[] {
  if (getCustomTrackLayout(trackId)) return [];
  const trackLength = getTrackLength(trackId);
  const lanes = HAZARD_LANES[variant?.hazardLayout ?? "paired"];
  return HAZARD_LAYOUT.map(([progress], index) => {
    const lateral = lanes[index];
    const distance = trackLength * progress;
    const position = worldPosition(distance, lateral, 0.12, trackId);
    return { id: `foam-v${variant?.index ?? 0}-${index}`, kind: "foam", ownerId: null, ...position, radius: 2.65, distance, lateral, respawnAt: 0 };
  });
}

export function stepKartHazards(race: RaceState, kart: KartState, dt: number): void {
  kart.hazardCooldown = Math.max(0, kart.hazardCooldown - dt);
  // Beer Goggles makes the kart phase through course hazards and dropped
  // puddles for the full authoritative timer.
  if (kart.airborne || kart.ghostTime > 0 || kart.hazardCooldown > 0) return;
  for (const hazard of race.hazards) {
    if ((hazard.expiresAt !== undefined && hazard.expiresAt <= race.elapsed) || hazard.respawnAt > race.elapsed || Math.abs(kart.y - hazard.y) > 2.5 || Math.hypot(kart.x - hazard.x, kart.z - hazard.z) > hazard.radius) continue;
    if (hazard.kind === "spill") {
      // A Spill is a bounded, one-touch loss of grip: it trims speed and adds
      // a small deterministic yaw wobble, but does not become another Foam
      // Slick spin/guard alias.
      const intensity = Math.min(1, dt * 8);
      const slowdown = 1 - 0.6 * intensity;
      kart.velocityX *= slowdown;
      kart.velocityZ *= slowdown;
      kart.speed *= slowdown;
      const seed = Number(kart.id.replace(/\D/g, "")) || 1;
      kart.yaw += Math.sin(race.elapsed * 17 + seed * 0.73) * 0.14 * intensity;
      if (hazard.ownerId && hazard.ownerId !== kart.id) {
        const owner = race.karts.find((candidate) => candidate.id === hazard.ownerId);
        if (owner) { owner.itemHitCount += 1; owner.lastItemHit = "spill"; }
      }
      kart.hazardCooldown = 0.85;
      hazard.expiresAt = race.elapsed;
      break;
    }
    if (kart.shieldTime > 0) kart.shieldTime = 0;
    else {
      kart.spinTime = Math.max(kart.spinTime, 0.9);
      kart.velocityX *= 0.55;
      kart.velocityZ *= 0.55;
      if (hazard.ownerId && hazard.ownerId !== kart.id) {
        const owner = race.karts.find((candidate) => candidate.id === hazard.ownerId);
        if (owner) owner.itemHitCount += 1;
      }
    }
    kart.hazardCooldown = 1.2;
    if (hazard.expiresAt !== undefined) hazard.expiresAt = race.elapsed;
    else hazard.respawnAt = race.elapsed + 4;
    break;
  }
}
